import Title from "antd/es/typography/Title";
import React, { FC, useMemo, useState } from "react";
import { FormInput, FormSelect, RadioButtons } from "shared/ui";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { UploadDocumentValues } from "shared/types";
import {
  MaterialStockBatchItemResponse,
  MaterialStockItem,
  useGetAllMaterialsQuery,
  useGetAllOrdersQuery,
} from "shared/api";
import { UploadFile } from "antd";
import { qualityResultsTypeValues } from "shared/constants";
import { adaptMaterials, adaptOrders } from "shared/adapters";
import { useDebouncedValue } from "shared/hooks";
import { UploadDocument } from "./UploadDocument";
import { StockBatchesTable } from "./StockBatchesTable";
import { MaterialItemsTable } from "./MaterialItemsTable";

interface RequiredQualityFieldsProps {
  handleDocument: (file: File | null) => void;
  handleStockBatchesRows: (
    stockBatches: MaterialStockBatchItemResponse[]
  ) => void;
  selectedStockBatchesKeys: React.Key[];
  handleStockBatchesKeys: (keys: React.Key[]) => void;
  handleMaterialItemsRows: (materialItems: MaterialStockItem[]) => void;
  selectedMaterialItemsKeys: React.Key[];
  handleMaterialItemsKeys: (keys: React.Key[]) => void;
  handleDocumentList: (files: UploadFile[]) => void;
  documentList: UploadFile[];
}

const RequiredQualityFields: FC<RequiredQualityFieldsProps> = ({
  handleDocument,
  handleStockBatchesRows,
  selectedStockBatchesKeys,
  handleStockBatchesKeys,
  handleMaterialItemsRows,
  selectedMaterialItemsKeys,
  handleMaterialItemsKeys,
  handleDocumentList,
  documentList,
}) => {
  const { t } = useTranslation();

  const { values, setFieldValue, handleBlur, handleChange, errors } =
    useFormikContext<UploadDocumentValues>();

  const [filteringVariant, setFilteringVariant] = useState(true);

  const [materialSearch, setMaterialSearch] = useState("");
  const debouncedMaterialSearch = useDebouncedValue(materialSearch, 400);

  const { data: materialsData, isLoading: isLoadingMaterials } =
    useGetAllMaterialsQuery(
      {
        limit: 20,
        name__icontains: debouncedMaterialSearch || undefined,
        expand: "suppliers.supplier",
      },
      { skip: !filteringVariant }
    );

  const adaptedMaterials = useMemo(
    () => adaptMaterials(materialsData?.results ?? []),
    [materialsData]
  );

  const [orderSearch, setOrderSearch] = useState("");
  const debouncedOrderSearch = useDebouncedValue(orderSearch, 400);

  const { data: ordersData, isLoading: isLoadingOrders } = useGetAllOrdersQuery(
    { limit: 20, order_code__icontains: debouncedOrderSearch || undefined },
    { skip: filteringVariant }
  );

  const adaptedOrders = useMemo(
    () => adaptOrders(ordersData ?? []),
    [ordersData]
  );

  const handleMaterialAsFilter = () => {
    setFilteringVariant(true);
    setFieldValue("order", "");
    handleStockBatchesKeys([]);
    handleMaterialItemsKeys([]);
    handleStockBatchesRows([]);
    handleMaterialItemsRows([]);
  };

  const handleOrderAsFilter = () => {
    setFilteringVariant(false);
    setFieldValue("material", "");
    handleStockBatchesKeys([]);
    handleMaterialItemsKeys([]);
    handleStockBatchesRows([]);
    handleMaterialItemsRows([]);
  };

  return (
    <>
      <Title level={5}>{t("quality.name")}</Title>

      <FormInput
        value={values?.name}
        fieldName="name"
        placeholder={t("quality.name")}
        handleBlur={handleBlur}
        handleChange={handleChange}
        error={errors?.name}
        isError={!!errors?.name}
      />

      <Title level={5}>{t("quality.select-type")}</Title>

      <FormSelect
        value={values?.result_type}
        fieldName="result_type"
        placeholder={t("quality.select-type")}
        setFieldValue={setFieldValue}
        handleBlur={handleBlur}
        options={qualityResultsTypeValues}
        error={errors?.result_type}
        isError={!!errors?.result_type}
      />

      <UploadDocument
        handleDocument={handleDocument}
        handleDocumentList={handleDocumentList}
        documentList={documentList}
      />

      <RadioButtons
        isActive={filteringVariant}
        selectLeftOption={handleMaterialAsFilter}
        selectRightOption={handleOrderAsFilter}
        title={t("quality.filter-by")}
        leftVariantTitle={t("quality.material")}
        rightVariantTitle={t("quality.order")}
      />

      {filteringVariant ? (
        <>
          <Title level={5}>{t("quality.select-material")}</Title>

          <FormSelect
            value={values?.material || null}
            fieldName="material"
            placeholder={t("quality.select-material")}
            setFieldValue={setFieldValue}
            handleBlur={handleBlur}
            options={adaptedMaterials}
            showSearch
            isLoading={isLoadingMaterials}
            onSearch={setMaterialSearch}
            searchHint="If your material is not in the list, please use the search"
          />
        </>
      ) : (
        <>
          <Title level={5}>{t("quality.select-order")}</Title>

          <FormSelect
            value={values?.order || null}
            fieldName="order"
            placeholder={t("quality.select-order")}
            setFieldValue={setFieldValue}
            handleBlur={handleBlur}
            options={adaptedOrders}
            showSearch
            isLoading={isLoadingOrders}
            onSearch={setOrderSearch}
            searchHint="If your order is not in the list, please use the search"
          />
        </>
      )}

      <StockBatchesTable
        handleStockBatchesRows={handleStockBatchesRows}
        selectedStockBatchesKeys={selectedStockBatchesKeys}
        handleStockBatchesKeys={handleStockBatchesKeys}
      />

      {selectedStockBatchesKeys?.length ? (
        <MaterialItemsTable
          handleMaterialItemsRows={handleMaterialItemsRows}
          selectedMaterialItemsKeys={selectedMaterialItemsKeys}
          handleMaterialItemsKeys={handleMaterialItemsKeys}
          selectedStockBatchesKeys={selectedStockBatchesKeys}
        />
      ) : null}
    </>
  );
};

export { RequiredQualityFields };
